import { useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, RefObject } from 'react';
import { TAG_COLOR_IDS, TAG_COLORS, tagTones } from '../design/tagPalette';
import type { TagColorId } from '../design/tagPalette';
import type { Theme } from '../design/tokens';
import { CardHang, pageSize } from '../plane/Hang';
import { isStickyTint } from '../plane/planeKey';
import type { PlanePlacement, StickyTint } from '../plane/planeKey';
import type { TableState } from '../plane/useTable';
import { hrefFor } from '../routes/EntityPage';
import type { MintedNote } from '../writing/useNoteText';
import { NookWriting } from './NookWriting';
import { PANEL_MAX_H, PANEL_MIN, fitPanelSize, keepPanelSize, panelSizeOf } from './notePanelSize';
import type { PanelSize } from './notePanelSize';
import './nook.css';

/** What a note is drawn in: its sticky's own fill, and the panel's chip and edge to match. */
export interface NoteTone {
  /** The sticky's fill. */
  fill: string;
  chip: string;
  edge: string;
}

/**
 * The colour a sticky wears: its tag colour where it has one, else the nook's own tag colour while
 * it has no sticky word of its own, else its warm or green word.
 */
export function noteTone(
  placement: Pick<PlanePlacement, 'tint' | 'tagTint'> | undefined,
  inherited: TagColorId | null,
  theme: Theme,
): NoteTone {
  const tag = placement?.tagTint ?? (placement?.tint === undefined ? inherited : null);
  if (tag) {
    const tones = tagTones(theme, tag);
    return { fill: tones.band, chip: tones.chip, edge: tones.edge };
  }
  const word = placement?.tint === 'green' ? 'var(--stickyGreen)' : 'var(--stickyWarm)';
  return { fill: word, chip: word, edge: 'var(--ruleStrong)' };
}

/** The colour a new sticky starts in, where nothing gives it one of its own. */
export const FIRST_TINT: TagColorId = 'fern';

/** A colour picked before the sticky stands, as the placement it will become. */
export function pendingTint(tint: StickyTint | TagColorId | undefined): Pick<PlanePlacement, 'tint' | 'tagTint'> | undefined {
  if (tint === undefined) return undefined;
  return isStickyTint(tint) ? { tint } : { tint: 'warm', tagTint: tint };
}

function Swatches({
  theme,
  current,
  onTint,
}: {
  theme: Theme;
  current: StickyTint | TagColorId | null;
  onTint: (tint: StickyTint | TagColorId) => void;
}) {
  const [open, setOpen] = useState(false);
  const host = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (!host.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', away, true);
    return () => document.removeEventListener('pointerdown', away, true);
  }, [open]);
  const colourOf = (id: StickyTint | TagColorId) =>
    id === 'warm' ? 'var(--stickyWarm)' : id === 'green' ? 'var(--stickyGreen)' : tagTones(theme, id).mark;
  const swatch = (id: StickyTint | TagColorId, label: string) => (
    <button
      key={id}
      type="button"
      className="hang-swatch"
      aria-label={label}
      title={label}
      aria-pressed={current === id}
      onClick={() => {
        onTint(id);
        setOpen(false);
      }}
    >
      <span className="hang-swatch-dot" style={{ background: colourOf(id) }} />
    </button>
  );
  return (
    <span className="hang-swatches" role="group" aria-label="Colour" ref={host}>
      <button
        type="button"
        className="hang-tint"
        aria-label="Colour"
        title="Colour"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        <span className="hang-tint-dot" style={{ background: current ? colourOf(current) : 'var(--stickyWarm)' }} />
      </button>
      {open && (
        <span className="hang-swatch-pop">
          {swatch('warm', 'Warm')}
          {swatch('green', 'Green')}
          {TAG_COLOR_IDS.map((id) => swatch(id, TAG_COLORS[id].name))}
        </span>
      )}
    </span>
  );
}

/** A note panel's size: what it was left at in this browser, fitted to the page, and the grip that
 *  pulls it to another. */
export function useNotePanelSize(path: string | null) {
  const [pulled, setPulled] = useState<{ path: string | null; size: PanelSize } | null>(null);
  const win = pageSize();
  const base = fitPanelSize(panelSizeOf(path ?? ''), win);
  const size = pulled && pulled.path === path ? pulled.size : base;

  // A size pulled before the first word made the note is the note's from then on.
  useEffect(() => {
    if (path && pulled && pulled.path === null) {
      keepPanelSize(path, pulled.size);
      setPulled({ path, size: pulled.size });
    }
  }, [path, pulled]);

  const onGripDown = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    const grip = event.currentTarget;
    grip.setPointerCapture(event.pointerId);
    const start = { x: event.clientX, y: event.clientY, size };
    const sized = (e: PointerEvent): PanelSize => ({
      w: Math.max(PANEL_MIN.w, Math.min(start.size.w + e.clientX - start.x, win.w > 0 ? win.w - 24 : Infinity)),
      h: Math.max(PANEL_MIN.h, Math.min(start.size.h + e.clientY - start.y, PANEL_MAX_H)),
    });
    const move = (e: PointerEvent) => setPulled({ path, size: sized(e) });
    const up = (e: PointerEvent) => {
      grip.removeEventListener('pointermove', move);
      grip.removeEventListener('pointerup', up);
      grip.removeEventListener('pointercancel', up);
      const end = sized(e);
      setPulled({ path, size: end });
      if (path) keepPanelSize(path, end);
    };
    grip.addEventListener('pointermove', move);
    grip.addEventListener('pointerup', up);
    grip.addEventListener('pointercancel', up);
  };

  return { size, onGripDown };
}

/**
 * A note opened off its sticky: the editor, in the note's own tint, at the size it was left. The
 * sticky is not touched by any of it — the panel's size is this browser's, and the tint it picks is
 * the one the sticky wears. A note not made yet hangs off [at], where its sticky will be, and is
 * made by its first word.
 */
export function StickyPanel({
  table,
  surface,
  theme,
  path,
  cardKey,
  at,
  tone,
  tint,
  onTint,
  mint,
  onChange,
  onClose,
}: {
  table: TableState;
  surface: RefObject<HTMLElement | null>;
  theme: Theme;
  /** Null until the first word makes the note. */
  path: string | null;
  cardKey: string | null;
  /** Where its sticky will stand, while there is none. */
  at: { x: number; y: number } | null;
  tone: NoteTone;
  /** The colour picked now, for the swatch that shows it. */
  tint: StickyTint | TagColorId | null;
  onTint: (tint: StickyTint | TagColorId) => void;
  mint: (text: string) => MintedNote | null;
  onChange: (path: string, raw: string) => void;
  onClose: () => void;
}) {
  const { size, onGripDown } = useNotePanelSize(path);

  const writing = (
    <NookWriting
      path={path}
      docked={false}
      hung
      swatches={<Swatches theme={theme} current={tint} onTint={onTint} />}
      fullScreen={path ? { to: hrefFor(path) } : undefined}
      onGripDown={onGripDown}
      onToggleDock={() => {}}
      onClose={onClose}
      mint={mint}
      onChange={onChange}
    />
  );
  return (
    <CardHang
      table={table}
      surface={surface}
      cardKey={cardKey}
      fallback={at && { at, card: { w: 220, h: 80 } }}
      kind="note"
      label="Note"
      size={size}
      tint={tone}
      className="hang-sticky"
    >
      {writing}
    </CardHang>
  );
}
